import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { makeT } from "@/lib/i18n";
import { FollowUpExchange } from "@/components/FollowUpExchange";
import { useAuth } from "@/context/AuthContext";
import { UserMenu } from "@/components/UserMenu";
import { PolishedAnswerText } from "@/components/PolishedAnswerText";
import ProfanityNote from "@/components/ProfanityNote";
import {
  Star,
  CheckCircle,
  Warning,
  ChartBar,
  DownloadSimple,
  LockSimple,
  ArrowClockwise,
  ChatCircleDots,
} from "@phosphor-icons/react";

export const COPY = {
  en: {
    eyebrow: "Free Limited Trial",
    title: "Your interview results",
    intro: "Here is the feedback for every question in your free interview, so you can see how the Interview Simulator evaluates and coaches your answers.",
    question: "Question",
    overall: "Overall",
    performance: "Interview Performance",
    english: "English Language",
    transcript: "Your Transcript",
    suggested: "Suggested Answer",
    strengths: "Strengths",
    improve: "How to Improve",
    noStrengths: "No specific strengths were identified in this response.",
    noImprovements: "No specific improvement points were identified in this response.",
    relevance: "Relevance",
    evidence: "Supporting Evidence",
    organization: "Organization",
    competency: "Professional Competency",
    results: "Evidence of Results",
    presence: "Professional Presence",
    grammar: "Grammar",
    vocabulary: "Vocabulary",
    clarity: "Clarity",
    tone: "Professional Tone",
    fluency: "Fluency",
    insufficient: "There was not enough spoken English in this answer to assess the English-language dimensions reliably.",
    locked: "The downloadable interview report and Re-Practice come with 30-day full access.",
    notSaved: "Your results aren’t saved. They’re deleted when you leave this page or log out, and automatically after 24 hours.",
    b1Title: "A note about your English level",
    b1Body: "Based on this interview, your spoken English is currently below B1, the level this app is designed for. From B1, you can practise full interview answers and get the most from the feedback. We recommend building your general English first, for example with an English course or teacher, and coming back when you feel ready. You’re welcome to continue if you choose.",
    report: "Download Interview Report",
    paidOnly: "Available with 30-day full access",
    repractice: "Practice these questions again",
    closing: "Your questions for the interviewer",
    home: "Return to account home",
  },
  "pt-BR": {
    eyebrow: "Teste Gratuito Limitado",
    title: "Os resultados da sua entrevista",
    intro: "Aqui está o feedback de cada pergunta da sua entrevista gratuita, para você ver como o Interview Simulator avalia e orienta suas respostas.",
    question: "Pergunta",
    overall: "Geral",
    performance: "Desempenho na Entrevista",
    english: "Língua Inglesa",
    transcript: "Sua Transcrição",
    suggested: "Resposta Sugerida",
    strengths: "Pontos Fortes",
    improve: "Como Melhorar",
    noStrengths: "Nenhum ponto forte específico foi identificado nesta resposta.",
    noImprovements: "Nenhum ponto específico de melhoria foi identificado nesta resposta.",
    relevance: "Relevância",
    evidence: "Evidências de Apoio",
    organization: "Organização",
    competency: "Competência Profissional",
    results: "Evidência de Resultados",
    presence: "Presença Profissional",
    grammar: "Gramática",
    vocabulary: "Vocabulário",
    clarity: "Clareza",
    tone: "Tom Profissional",
    fluency: "Fluência",
    insufficient: "Não houve inglês falado suficiente nesta resposta para avaliar com segurança os critérios de idioma.",
    locked: "O relatório da entrevista para download e a nova prática fazem parte do acesso completo de 30 dias.",
    notSaved: "Seus resultados não são salvos. Eles são excluídos quando você sai desta página ou faz logout, e automaticamente após 24 horas.",
    b1Title: "Uma observação sobre o seu nível de inglês",
    b1Body: "Com base nesta entrevista, o seu inglês falado está atualmente abaixo do B1, o nível para o qual este aplicativo foi criado. A partir do B1, você pode praticar respostas completas de entrevista e aproveitar melhor o feedback. Recomendamos desenvolver primeiro o seu inglês geral, por exemplo com um curso ou professor de inglês, e voltar quando se sentir pronto. Se preferir, você pode continuar.",
    report: "Baixar Relatório da Entrevista",
    paidOnly: "Disponível com o acesso completo de 30 dias",
    repractice: "Praticar estas perguntas novamente",
    closing: "Suas perguntas para o entrevistador",
    home: "Voltar à página inicial da conta",
  },
  "es-419": {
    eyebrow: "Prueba Gratuita Limitada",
    title: "Los resultados de su entrevista",
    intro: "Aquí están los comentarios de cada pregunta de su entrevista gratuita, para que vea cómo Interview Simulator evalúa y orienta sus respuestas.",
    question: "Pregunta",
    overall: "General",
    performance: "Desempeño en la Entrevista",
    english: "Idioma Inglés",
    transcript: "Su Transcripción",
    suggested: "Respuesta Sugerida",
    strengths: "Fortalezas",
    improve: "Cómo Mejorar",
    noStrengths: "No se identificaron fortalezas específicas en esta respuesta.",
    noImprovements: "No se identificaron puntos específicos de mejora en esta respuesta.",
    relevance: "Relevancia",
    evidence: "Evidencia de Apoyo",
    organization: "Organización",
    competency: "Competencia Profesional",
    results: "Evidencia de Resultados",
    presence: "Presencia Profesional",
    grammar: "Gramática",
    vocabulary: "Vocabulario",
    clarity: "Claridad",
    tone: "Tono Profesional",
    fluency: "Fluidez",
    insufficient: "No hubo suficiente inglés hablado en esta respuesta para evaluar de forma confiable las dimensiones del idioma.",
    locked: "El informe de la entrevista descargable y la nueva práctica forman parte del acceso completo de 30 días.",
    notSaved: "Sus resultados no se guardan. Se eliminan cuando sale de esta página o cierra sesión, y automáticamente después de 24 horas.",
    b1Title: "Una nota sobre su nivel de inglés",
    b1Body: "Según esta entrevista, su inglés hablado está actualmente por debajo del B1, el nivel para el que se diseñó esta aplicación. Desde el B1, puede practicar respuestas completas de entrevista y aprovechar mejor los comentarios. Le recomendamos desarrollar primero su inglés general, por ejemplo con un curso o un profesor de inglés, y volver cuando se sienta listo. Si lo prefiere, puede continuar.",
    report: "Descargar Informe de Entrevista",
    paidOnly: "Disponible con el acceso completo de 30 días",
    repractice: "Practicar estas preguntas nuevamente",
    closing: "Sus preguntas para el entrevistador",
    home: "Volver a la página principal de la cuenta",
  },
  "fr-CA": {
    eyebrow: "Essai Gratuit Limité",
    title: "Les résultats de votre entrevue",
    intro: "Voici la rétroaction pour chaque question de votre entrevue gratuite, afin de vous montrer comment Interview Simulator évalue et améliore vos réponses.",
    question: "Question",
    overall: "Global",
    performance: "Performance en Entrevue",
    english: "Langue Anglaise",
    transcript: "Votre Transcription",
    suggested: "Réponse Suggérée",
    strengths: "Points Forts",
    improve: "Comment S'améliorer",
    noStrengths: "Aucun point fort précis n’a été relevé dans cette réponse.",
    noImprovements: "Aucun point d’amélioration précis n’a été relevé dans cette réponse.",
    relevance: "Pertinence",
    evidence: "Éléments à l'Appui",
    organization: "Organisation",
    competency: "Compétence Professionnelle",
    results: "Preuve de Résultats",
    presence: "Présence Professionnelle",
    grammar: "Grammaire",
    vocabulary: "Vocabulaire",
    clarity: "Clarté",
    tone: "Ton Professionnel",
    fluency: "Fluidité",
    insufficient: "Il n'y avait pas assez d'anglais parlé dans cette réponse pour évaluer de façon fiable les dimensions linguistiques.",
    locked: "Le rapport d’entrevue téléchargeable et la nouvelle pratique sont offerts avec l’accès complet de 30 jours.",
    notSaved: "Vos résultats ne sont pas conservés. Ils sont supprimés quand vous quittez cette page ou vous déconnectez, et automatiquement après 24 heures.",
    b1Title: "Une note sur votre niveau d’anglais",
    b1Body: "D’après cette entrevue, votre anglais parlé est actuellement sous le niveau B1, le niveau pour lequel cette application est conçue. À partir du B1, vous pouvez pratiquer des réponses d’entrevue complètes et tirer le meilleur parti de la rétroaction. Nous vous recommandons de renforcer d’abord votre anglais général, par exemple avec un cours ou un professeur d’anglais, et de revenir quand vous vous sentirez prêt. Vous pouvez continuer si vous le souhaitez.",
    report: "Télécharger le Rapport d'Entrevue",
    paidOnly: "Offert avec l’accès complet de 30 jours",
    repractice: "Pratiquer de nouveau ces questions",
    closing: "Vos questions pour l’intervieweur",
    home: "Retourner à l'accueil du compte",
  },
};

function scoreColor(v) {
  if (v >= 75) return "var(--score-good)";
  if (v >= 50) return "var(--score-average)";
  return "var(--score-bad)";
}

function ScoreBar({ label, value }) {
  const isNA = value === null || value === undefined;
  const pct = isNA ? 0 : Number(value);
  return (
    <div>
      <div className="flex items-center justify-between text-sm">
        <span className="text-[color:var(--text-secondary)] uppercase tracking-widest text-[10px]">{label}</span>
        <span className="mono font-medium">{isNA ? "—" : value}</span>
      </div>
      <div className="mt-1.5 h-1.5 rounded-full bg-[color:var(--border-stone)] overflow-hidden">
        <div className="h-full rounded-full" style={{ width: isNA ? "0%" : `${pct}%`, background: isNA ? "transparent" : scoreColor(pct) }} />
      </div>
    </div>
  );
}

export default function TrialReport() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/sessions/${sessionId}/trial-report-preview`)
      .then(({ data: payload }) => {
        if (alive) setData(payload || {});
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
      // Free Account standards (2026-10-30): the results are deleted when the user leaves.
      api.post(`/sessions/${sessionId}/trial-results-close`).catch(() => {});
    };
  }, [sessionId]);

  const lang = data?.interface_language || user?.preferred_language || data?.language || "en";
  const t = COPY[lang] || COPY.en;

  if (failed) {
    return <div className="min-h-screen flex items-center justify-center"><button type="button" onClick={() => navigate("/")} className="rounded-full bg-[color:var(--brand)] text-white px-6 py-3">{t.home}</button></div>;
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center text-[color:var(--text-secondary)]">{makeT(lang)("iv.loading")}</div>;
  }

  const answerFor = (i) => (data.answers || []).find((a) => Number(a.question_index) === i);

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)]" data-testid="trial-report-preview">
      <header className="border-b border-[color:var(--border-stone)] bg-white/80 backdrop-blur-xl">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <img src="/brand/logo.png" alt="English Job Interview Simulator" className="h-[63px] w-[63px] object-contain" />
            <div className="min-w-0">
              <div className="font-semibold tracking-tight truncate">English Job Interview Simulator</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--brand)] mt-1">{t.eyebrow}</div>
            </div>
          </div>
          <UserMenu />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-8 md:px-12 py-8 sm:py-12">
        <section className="rounded-3xl border border-[color:var(--border-stone)] bg-white p-6 sm:p-9">
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight">{t.title}</h1>
          <p className="mt-4 max-w-3xl text-[color:var(--text-secondary)] leading-relaxed">{t.intro}</p>
        </section>

        {data.below_b1 && (
          <section className="mt-8 rounded-3xl border border-[color:var(--score-average)]/40 bg-white p-6 sm:p-8" role="note" data-testid="trial-below-b1">
            <h2 className="text-lg font-semibold">{t.b1Title}</h2>
            <p className="mt-3 text-sm leading-relaxed text-[color:var(--text-secondary)]">{t.b1Body}</p>
          </section>
        )}

        {(data.questions || []).map((q, i) => (
          <QuestionFeedback key={i} index={i} q={q} a={answerFor(i)} t={t} lang={lang} />
        ))}

        <section className="mt-8 rounded-3xl border border-[color:var(--brand)]/25 bg-white p-6 sm:p-8" data-testid="trial-report-paywall">
          <div className="flex items-start gap-3">
            <LockSimple size={24} weight="duotone" className="text-[color:var(--brand)] shrink-0 mt-0.5" />
            <p className="font-semibold text-lg leading-relaxed">{t.locked}</p>
          </div>
          <p className="mt-3 text-sm text-[color:var(--text-secondary)]" data-testid="trial-not-saved">{t.notSaved}</p>

          {/* Paid features are shown greyed out, never hidden (design principle, 2026-10-30). */}
          <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <LockedDownload icon={ChartBar} title={t.report} subtitle={t.paidOnly} />
            <LockedDownload icon={ArrowClockwise} title={t.repractice} subtitle={t.paidOnly} download={false} testId="trial-locked-repractice" />
            {/* S5: "Do you have any questions for me?" comes with full access. */}
            <LockedDownload icon={ChatCircleDots} title={t.closing} subtitle={t.paidOnly} download={false} testId="trial-locked-closing" />
          </div>

          <button type="button" onClick={() => navigate("/", { replace: true })} className="mt-7 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white px-6 py-3 text-sm font-medium">{t.home}</button>
        </section>
      </main>
    </div>
  );
}

function LockedDownload({ icon: Icon, title, subtitle, download = true, testId }) {
  return (
    <div className="rounded-2xl border border-[color:var(--border-stone)] bg-[color:var(--bg-surface-hover)] p-5 opacity-60" aria-disabled="true" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-full bg-[color:var(--border-stone)] flex items-center justify-center shrink-0"><Icon weight="duotone" size={22} /></div>
        <div className="font-semibold">{title}</div>
      </div>
      <button type="button" disabled className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-slate-300 text-slate-600 px-5 py-3 text-sm font-medium cursor-not-allowed"><LockSimple size={16} />{download && <DownloadSimple size={16} />}{subtitle}</button>
    </div>
  );
}

// One question's feedback (all six are shown since 2026-10-30; before, only Question 1).
function QuestionFeedback({ index, q, a, t, lang }) {
  const ev = a?.evaluation || null;
  const pt = String(lang).toLowerCase().startsWith("pt") ? ev?.pt_br : null;
  const strengths = (pt?.strengths ?? ev?.strengths) || [];
  const improvements = (pt?.improvements ?? ev?.improvements) || [];
  return (
    <section className="mt-8 rounded-3xl border border-[color:var(--border-stone)] bg-white p-6 sm:p-8" data-testid="trial-question-feedback">
      <div className="flex items-start justify-between gap-6">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--brand)]">{t.question} {index + 1}{q.focus ? ` · ${q.focus}` : ""}</div>
          <div className="mt-2 text-lg font-semibold tracking-tight leading-snug">{q.text}</div>
        </div>
        {ev && (
          <div className="text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)]">{t.overall}</div>
            <div className="mono text-3xl font-semibold" style={{ color: scoreColor(ev.overall) }}>{ev.overall}</div>
          </div>
        )}
      </div>

      {ev && (
        <>
          <div className="mt-7">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">{t.performance}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              <ScoreBar label={t.relevance} value={ev.relevance} />
              <ScoreBar label={t.evidence} value={ev.supporting_evidence} />
              <ScoreBar label={t.organization} value={ev.organization} />
              <ScoreBar label={t.competency} value={ev.professional_competency} />
              <ScoreBar label={t.results} value={ev.evidence_of_results} />
              <ScoreBar label={t.presence} value={ev.professional_presence} />
            </div>
          </div>

          <div className="mt-7">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">{t.english}</div>
            {ev.english_insufficient ? (
              <div className="text-sm italic text-[color:var(--text-secondary)] rounded-2xl bg-[color:var(--bg-surface-hover)] px-5 py-4 border border-dashed border-[color:var(--border-stone)]">{t.insufficient}</div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                <ScoreBar label={t.grammar} value={ev.grammar} />
                <ScoreBar label={t.vocabulary} value={ev.vocabulary} />
                <ScoreBar label={t.clarity} value={ev.clarity} />
                <ScoreBar label={t.tone} value={ev.professional_tone} />
                <ScoreBar label={t.fluency} value={ev.fluency} />
              </div>
            )}
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">{t.transcript}</div>
              <div className="rounded-2xl bg-[color:var(--bg-surface-hover)] p-5 text-sm leading-relaxed">{a?.transcript || "—"}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">{t.suggested}</div>
              <div className="rounded-2xl bg-[color:var(--brand)]/5 border border-[color:var(--brand)]/20 p-5 text-sm leading-relaxed"><PolishedAnswerText text={ev.suggested_answer} fallback="—" /></div>
            </div>
          </div>

          {ev.profanity && <ProfanityNote text={makeT(lang)("rp.profanityNote")} testId="trial-profanity-note" />}

          <FollowUpExchange answer={a} t={makeT(lang)} testId="trial-follow-up" />

          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 font-semibold text-[color:var(--brand)] mb-3"><Star weight="duotone" /> {t.strengths}</div>
              {strengths.length > 0 ? (
                <ul className="space-y-2">{strengths.map((s, i) => <li key={i} className="flex gap-3 text-sm"><CheckCircle size={16} weight="fill" className="mt-0.5 text-[color:var(--brand)] shrink-0" /><span>{s}</span></li>)}</ul>
              ) : (
                <p className="text-sm italic text-[color:var(--text-secondary)]">{t.noStrengths}</p>
              )}
            </div>
            <div>
              <div className="flex items-center gap-2 font-semibold text-[color:var(--score-bad)] mb-3"><Warning weight="duotone" /> {t.improve}</div>
              {improvements.length > 0 ? (
                <ul className="space-y-2">{improvements.map((s, i) => <li key={i} className="flex gap-3 text-sm"><span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[color:var(--score-bad)] shrink-0" /><span>{s}</span></li>)}</ul>
              ) : (
                <p className="text-sm italic text-[color:var(--text-secondary)]">{t.noImprovements}</p>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
